import { generateId } from "./tournamentLogic";
import {
  League,
  RoundRobinLeague,
  LeagueMatch,
  LeagueWeek,
  LeagueStanding,
  LeagueStats,
  LeagueCreationOptions,
} from "../types/league";
import { Team, GameResult } from "../types/tournament";

// Create Round Robin League
export const createRoundRobinLeague = (
  options: LeagueCreationOptions
): RoundRobinLeague => {
  const { name, teams, settings } = options;

  // Calculate total weeks needed for round robin
  const totalWeeks = teams.length % 2 === 0 ? teams.length - 1 : teams.length;

  // Generate round robin schedule
  const weeks = generateRoundRobinSchedule(teams, totalWeeks);

  // Initialize standings
  const standings = teams.map((team) => ({
    team,
    wins: 0,
    losses: 0,
    points: 0,
    gamesPlayed: 0,
    gamesWon: 0,
    gamesLost: 0,
    winPercentage: 0,
    pointsPerGame: 0,
    streak: 0,
    lastResult: null as "W" | "L" | null,
  }));

  // Initialize stats
  const stats: LeagueStats = {
    totalMatches: weeks.reduce((total, week) => total + week.matches.length, 0),
    completedMatches: 0,
    totalWeeks,
    completedWeeks: 0,
    averagePointsPerGame: 0,
    mostWins: null,
    mostPoints: null,
    longestStreak: null,
    perfectGames: 0,
    achievements: [],
  };

  // Generate court configurations (default to 4 courts for leagues)
  const courtConfigs = Array.from({ length: 4 }, (_, i) => ({
    id: `court-${i + 1}`,
    name: `Court ${i + 1}`,
    isActive: true,
  }));

  return {
    id: generateId(),
    name,
    teams,
    weeks,
    currentWeek: 1,
    totalWeeks,
    courts: courtConfigs,
    playerConfig: {
      playersPerTeam: 2,
      minTeamsRequired: 4,
      maxTeamsAllowed: 20,
      totalPlayersExpected: teams.length * 2,
      allowSubstitutes: false,
      maxSubstitutesPerTeam: 0,
    },
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
    leagueType: "round_robin",
    standings,
    stats,
    settings: {
      pointsForWin: 3,
      pointsForCloseLoss: 1,
      pointsForLoss: 0,
      closeLossThreshold: 2,
      seasonStartDate: settings.seasonStartDate || new Date(),
      seasonEndDate:
        settings.seasonEndDate ||
        new Date(Date.now() + totalWeeks * 7 * 24 * 60 * 60 * 1000),
    },
  };
};

// Placeholder functions for other league types
export const createDivisionLeague = (
  options: LeagueCreationOptions
): League => {
  // TODO: Implement division league creation
  const numDivisions = Math.ceil(options.teams.length / 6); // Assume max 6 teams per division
  const teamsPerDivision = Math.ceil(options.teams.length / numDivisions);

  // Create divisions
  const divisions = [];
  for (let i = 0; i < numDivisions; i++) {
    const divisionTeams = options.teams.slice(
      i * teamsPerDivision,
      (i + 1) * teamsPerDivision
    );
    divisions.push({
      id: generateId(),
      name: `Division ${String.fromCharCode(65 + i)}`, // A, B, C, etc.
      teams: divisionTeams,
      standings: divisionTeams.map((team) => ({
        team,
        wins: 0,
        losses: 0,
        points: 0,
        gamesPlayed: 0,
        gamesWon: 0,
        gamesLost: 0,
        winPercentage: 0,
        pointsPerGame: 0,
        streak: 0,
        lastResult: null,
      })),
      isActive: true,
    });
  }

  return {
    id: generateId(),
    name: options.name,
    divisions,
    weeks: [],
    currentWeek: 1,
    totalWeeks: options.settings.totalWeeks || 10,
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
    leagueType: "division",
    stats: {
      totalMatches: 0,
      completedMatches: 0,
      totalWeeks: options.settings.totalWeeks || 10,
      completedWeeks: 0,
      averagePointsPerGame: 0,
      mostWins: null,
      mostPoints: null,
      longestStreak: null,
      perfectGames: 0,
      achievements: [],
    },
    settings: {
      numberOfDivisions: numDivisions,
      teamsPerDivision,
      intraDivisionWeeks: 6,
      interDivisionWeeks: 3,
      playoffWeeks: 1,
      seasonStartDate: options.settings.seasonStartDate || new Date(),
      seasonEndDate:
        options.settings.seasonEndDate ||
        new Date(
          Date.now() +
            (options.settings.totalWeeks || 10) * 7 * 24 * 60 * 60 * 1000
        ),
    },
  };
};

export const createLadderLeague = (options: LeagueCreationOptions): League => {
  // TODO: Implement ladder league creation
  // Shuffle teams randomly for initial ladder positions
  const shuffledTeams = [...options.teams].sort(() => Math.random() - 0.5);

  return {
    id: generateId(),
    name: options.name,
    teams: options.teams,
    ladder: shuffledTeams, // Initial random order
    challenges: [],
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
    leagueType: "ladder",
    stats: {
      totalMatches: 0,
      completedMatches: 0,
      totalWeeks: 0,
      completedWeeks: 0,
      averagePointsPerGame: 0,
      mostWins: null,
      mostPoints: null,
      longestStreak: null,
      perfectGames: 0,
      achievements: [],
    },
    settings: {
      challengeWindow: 7, // 7 days to respond
      maxChallengesPerWeek: 2,
      challengeRange: 3, // can challenge teams within 3 positions
      seasonLength: 6, // 6 months
      monthlyChampions: [],
    },
    currentMonth: {
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      champion: null,
      startDate: new Date(),
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    },
  };
};

export const createFlexLeague = (options: LeagueCreationOptions): League => {
  // TODO: Implement flex league creation
  const requiredMatches = Math.max(options.teams.length - 1, 6); // Play at least 6 matches or against every other team

  return {
    id: generateId(),
    name: options.name,
    teams: options.teams,
    matches: [],
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
    leagueType: "flex",
    standings: options.teams.map((team) => ({
      team,
      wins: 0,
      losses: 0,
      points: 0,
      gamesPlayed: 0,
      gamesWon: 0,
      gamesLost: 0,
      winPercentage: 0,
      pointsPerGame: 0,
      streak: 0,
      lastResult: null,
    })),
    stats: {
      totalMatches: 0,
      completedMatches: 0,
      totalWeeks: 0,
      completedWeeks: 0,
      averagePointsPerGame: 0,
      mostWins: null,
      mostPoints: null,
      longestStreak: null,
      perfectGames: 0,
      achievements: [],
    },
    settings: {
      requiredMatches,
      periodLength: options.settings.totalWeeks || 8,
      currentPeriod: 1,
      periodStartDate: options.settings.seasonStartDate || new Date(),
      periodEndDate:
        options.settings.seasonEndDate ||
        new Date(
          Date.now() +
            (options.settings.totalWeeks || 8) * 7 * 24 * 60 * 60 * 1000
        ),
      allowSelfScheduling: true,
      maxMatchesPerWeek: 3,
    },
  };
};

export const createHybridLeague = (options: LeagueCreationOptions): League => {
  // TODO: Implement hybrid league creation
  const totalWeeks = options.settings.totalWeeks || 12;
  const poolPlayWeeks = 4;
  const ladderWeeks = 6;
  const playoffWeeks = 2;

  // Create initial pool phase
  const poolPhase = {
    id: generateId(),
    name: "Pool Play",
    type: "pool" as const,
    startWeek: 1,
    endWeek: poolPlayWeeks,
    isActive: true,
    isCompleted: false,
    teams: options.teams,
    matches: [],
    standings: options.teams.map((team) => ({
      team,
      wins: 0,
      losses: 0,
      points: 0,
      gamesPlayed: 0,
      gamesWon: 0,
      gamesLost: 0,
      winPercentage: 0,
      pointsPerGame: 0,
      streak: 0,
      lastResult: null,
    })),
  };

  const ladderPhase = {
    id: generateId(),
    name: "Ladder Play",
    type: "ladder" as const,
    startWeek: poolPlayWeeks + 1,
    endWeek: poolPlayWeeks + ladderWeeks,
    isActive: false,
    isCompleted: false,
    teams: [],
    matches: [],
    standings: [],
  };

  const playoffPhase = {
    id: generateId(),
    name: "Playoffs",
    type: "playoff" as const,
    startWeek: poolPlayWeeks + ladderWeeks + 1,
    endWeek: totalWeeks,
    isActive: false,
    isCompleted: false,
    teams: [],
    matches: [],
    standings: [],
  };

  return {
    id: generateId(),
    name: options.name,
    phases: [poolPhase, ladderPhase, playoffPhase],
    currentPhase: 0, // Start with pool play
    currentWeek: 1,
    totalWeeks,
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
    leagueType: "hybrid",
    stats: {
      totalMatches: 0,
      completedMatches: 0,
      totalWeeks,
      completedWeeks: 0,
      averagePointsPerGame: 0,
      mostWins: null,
      mostPoints: null,
      longestStreak: null,
      perfectGames: 0,
      achievements: [],
    },
    settings: {
      poolPlayWeeks,
      ladderWeeks,
      playoffWeeks,
      poolSize: 4,
      seasonStartDate: options.settings.seasonStartDate || new Date(),
      seasonEndDate:
        options.settings.seasonEndDate ||
        new Date(Date.now() + totalWeeks * 7 * 24 * 60 * 60 * 1000),
    },
  };
};

export const createKingOfCourtLeague = (
  options: LeagueCreationOptions
): League => {
  // TODO: Implement king of court league creation
  const totalSessions = (options.settings.totalWeeks || 10) * 2; // 2 sessions per week

  return {
    id: generateId(),
    name: options.name,
    teams: options.teams,
    sessions: [],
    currentSession: 1,
    totalSessions,
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
    leagueType: "king_of_court",
    stats: {
      totalMatches: 0,
      completedMatches: 0,
      totalWeeks: options.settings.totalWeeks || 10,
      completedWeeks: 0,
      averagePointsPerGame: 0,
      mostWins: null,
      mostPoints: null,
      longestStreak: null,
      perfectGames: 0,
      achievements: [],
    },
    settings: {
      sessionDuration: 2, // 2 hours per session
      sessionsPerWeek: 2,
      pointsPerWin: 3,
      pointsPerDefense: 1,
      seasonLength: options.settings.totalWeeks || 10,
      seasonStartDate: options.settings.seasonStartDate || new Date(),
      seasonEndDate:
        options.settings.seasonEndDate ||
        new Date(
          Date.now() +
            (options.settings.totalWeeks || 10) * 7 * 24 * 60 * 60 * 1000
        ),
    },
    standings: options.teams.map((team) => ({
      team,
      wins: 0,
      losses: 0,
      points: 0,
      gamesPlayed: 0,
      gamesWon: 0,
      gamesLost: 0,
      winPercentage: 0,
      pointsPerGame: 0,
      streak: 0,
      lastResult: null,
    })),
  };
};

// Generate Round Robin Schedule using Circle Method
export const generateRoundRobinSchedule = (
  teams: Team[],
  totalWeeks: number
): LeagueWeek[] => {
  const weeks: LeagueWeek[] = [];
  const teamCount = teams.length;

  // If odd number of teams, add a "bye" team
  const teamsWithBye =
    teamCount % 2 === 1
      ? [...teams, { ...teams[0], id: "bye", name: "BYE" }]
      : teams;
  const adjustedTeamCount = teamsWithBye.length;

  for (let week = 1; week <= totalWeeks; week++) {
    const matches: LeagueMatch[] = [];

    // Generate matches for this week using circle method
    for (let i = 0; i < adjustedTeamCount / 2; i++) {
      const team1Index = i;
      const team2Index = adjustedTeamCount - 1 - i;

      // Skip matches involving the bye team
      if (
        teamsWithBye[team1Index].id !== "bye" &&
        teamsWithBye[team2Index].id !== "bye"
      ) {
        const match: LeagueMatch = {
          id: generateId(),
          week,
          team1: teamsWithBye[team1Index],
          team2: teamsWithBye[team2Index],
          result: null,
          isCompleted: false,
          scheduledDate: new Date(Date.now() + week * 7 * 24 * 60 * 60 * 1000), // Schedule for future weeks
        };
        matches.push(match);
      }
    }

    weeks.push({
      weekNumber: week,
      matches,
      isCompleted: false,
      startDate: new Date(Date.now() + (week - 1) * 7 * 24 * 60 * 60 * 1000),
      endDate: new Date(Date.now() + week * 7 * 24 * 60 * 60 * 1000),
    });

    // Rotate teams for next week (circle method)
    const lastTeam = teamsWithBye[adjustedTeamCount - 1];
    for (let i = adjustedTeamCount - 1; i > 1; i--) {
      teamsWithBye[i] = teamsWithBye[i - 1];
    }
    teamsWithBye[1] = lastTeam;
  }

  return weeks;
};

// Update match result and recalculate standings
export const updateLeagueMatch = (
  league: RoundRobinLeague,
  matchId: string,
  team1Score: number,
  team2Score: number
): RoundRobinLeague => {
  // Find the match
  let targetMatch: LeagueMatch | null = null;
  let targetWeekIndex = -1;
  let targetMatchIndex = -1;

  for (let weekIndex = 0; weekIndex < league.weeks.length; weekIndex++) {
    const week = league.weeks[weekIndex];
    for (let matchIndex = 0; matchIndex < week.matches.length; matchIndex++) {
      const match = week.matches[matchIndex];
      if (match.id === matchId) {
        targetMatch = match;
        targetWeekIndex = weekIndex;
        targetMatchIndex = matchIndex;
        break;
      }
    }
    if (targetMatch) break;
  }

  if (!targetMatch) return league;

  // Create game result
  const result: GameResult = {
    team1Score,
    team2Score,
    winner:
      team1Score > team2Score ? targetMatch.team1.id : targetMatch.team2.id,
    loser:
      team1Score > team2Score ? targetMatch.team2.id : targetMatch.team1.id,
    completed: true,
    isPerfectGame:
      (team1Score === 11 && team2Score === 0) ||
      (team2Score === 11 && team1Score === 0),
    isCloseMatch:
      Math.abs(team1Score - team2Score) <= league.settings.closeLossThreshold,
  };

  // Update match
  const updatedMatch: LeagueMatch = {
    ...targetMatch,
    result,
    isCompleted: true,
  };

  // Update weeks
  const updatedWeeks = [...league.weeks];
  updatedWeeks[targetWeekIndex] = {
    ...updatedWeeks[targetWeekIndex],
    matches: updatedWeeks[targetWeekIndex].matches.map((match, index) =>
      index === targetMatchIndex ? updatedMatch : match
    ),
  };

  // Recalculate standings
  const updatedStandings = calculateLeagueStandings(
    league.teams,
    updatedWeeks,
    league.settings
  );

  // Update stats
  const updatedStats = calculateLeagueStats(updatedWeeks, updatedStandings);

  return {
    ...league,
    weeks: updatedWeeks,
    standings: updatedStandings,
    stats: updatedStats,
    updatedAt: new Date(),
  };
};

// Calculate league standings
export const calculateLeagueStandings = (
  teams: Team[],
  weeks: LeagueWeek[],
  settings: RoundRobinLeague["settings"]
): LeagueStanding[] => {
  const standingsMap = new Map<string, LeagueStanding>();

  // Initialize standings for all teams
  teams.forEach((team) => {
    standingsMap.set(team.id, {
      team,
      wins: 0,
      losses: 0,
      points: 0,
      gamesPlayed: 0,
      gamesWon: 0,
      gamesLost: 0,
      winPercentage: 0,
      pointsPerGame: 0,
      streak: 0,
      lastResult: null,
    });
  });

  // Process all completed matches
  weeks.forEach((week) => {
    week.matches.forEach((match) => {
      if (match.isCompleted && match.result) {
        const team1Standing = standingsMap.get(match.team1.id);
        const team2Standing = standingsMap.get(match.team2.id);

        if (team1Standing && team2Standing) {
          // Update games played
          team1Standing.gamesPlayed++;
          team2Standing.gamesPlayed++;

          // Update games won/lost
          team1Standing.gamesWon += match.result.team1Score;
          team1Standing.gamesLost += match.result.team2Score;
          team2Standing.gamesWon += match.result.team2Score;
          team2Standing.gamesLost += match.result.team1Score;

          // Update wins/losses and points
          if (match.result.winner === match.team1.id) {
            team1Standing.wins++;
            team1Standing.points += settings.pointsForWin;
            team2Standing.losses++;

            // Check for close loss
            if (match.result.isCloseMatch) {
              team2Standing.points += settings.pointsForCloseLoss;
            } else {
              team2Standing.points += settings.pointsForLoss;
            }

            team1Standing.lastResult = "W";
            team2Standing.lastResult = "L";
          } else {
            team2Standing.wins++;
            team2Standing.points += settings.pointsForWin;
            team1Standing.losses++;

            // Check for close loss
            if (match.result.isCloseMatch) {
              team1Standing.points += settings.pointsForCloseLoss;
            } else {
              team1Standing.points += settings.pointsForLoss;
            }

            team2Standing.lastResult = "W";
            team1Standing.lastResult = "L";
          }
        }
      }
    });
  });

  // Calculate percentages and points per game
  standingsMap.forEach((standing) => {
    standing.winPercentage =
      standing.gamesPlayed > 0
        ? (standing.wins / standing.gamesPlayed) * 100
        : 0;
    standing.pointsPerGame =
      standing.gamesPlayed > 0 ? standing.points / standing.gamesPlayed : 0;
  });

  // Sort standings by points, then win percentage, then games won
  return Array.from(standingsMap.values()).sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.winPercentage !== a.winPercentage)
      return b.winPercentage - a.winPercentage;
    return b.gamesWon - a.gamesWon;
  });
};

// Calculate league statistics
export const calculateLeagueStats = (
  weeks: LeagueWeek[],
  standings: LeagueStanding[]
): LeagueStats => {
  const totalMatches = weeks.reduce(
    (total, week) => total + week.matches.length,
    0
  );
  const completedMatches = weeks.reduce(
    (total, week) =>
      total + week.matches.filter((match) => match.isCompleted).length,
    0
  );
  const completedWeeks = weeks.filter((week) =>
    week.matches.every((match) => match.isCompleted)
  ).length;

  const mostWins = standings.length > 0 ? standings[0] : null;
  const mostPoints = standings.length > 0 ? standings[0] : null;

  // Calculate perfect games
  let perfectGames = 0;
  weeks.forEach((week) => {
    week.matches.forEach((match) => {
      if (match.isCompleted && match.result && match.result.isPerfectGame) {
        perfectGames++;
      }
    });
  });

  return {
    totalMatches,
    completedMatches,
    totalWeeks: weeks.length,
    completedWeeks,
    averagePointsPerGame:
      standings.length > 0
        ? standings.reduce((total, s) => total + s.pointsPerGame, 0) /
          standings.length
        : 0,
    mostWins: mostWins?.team || null,
    mostPoints: mostPoints?.team || null,
    longestStreak: null, // TODO: Implement streak tracking
    perfectGames,
    achievements: [], // TODO: Implement achievements
  };
};

// Get current week matches
export const getCurrentWeekMatches = (
  league: RoundRobinLeague
): LeagueMatch[] => {
  const currentWeek = league.weeks.find(
    (week) => week.weekNumber === league.currentWeek
  );
  return currentWeek?.matches || [];
};

// Advance to next week
export const advanceToNextWeek = (
  league: RoundRobinLeague
): RoundRobinLeague => {
  if (league.currentWeek < league.totalWeeks) {
    return {
      ...league,
      currentWeek: league.currentWeek + 1,
      updatedAt: new Date(),
    };
  }
  return league;
};
